(ns manage.content-configuration-page.view.input
  (:require [clojure.set :refer [rename-keys]]
            [clojure.spec.alpha :as spec]
            [common.spec]
            [common.ui.store :as store]
            [common.ui.validation.view :refer [form-field]]
            [common.util :as util]
            [common.widgets.datepicker :refer [datetime-picker time-picker]]
            [common.widgets.dropdown :as dropdown]
            [common.widgets.input :refer [input]]
            [common.widgets.tag-input :refer [tag-input]]
            [re-frame.core :as rf]
            [re-frame.interop :as rfi]
            [reagent.core :as r]
            [uix.core :refer [$]]))

(spec/def ::fn-or-event (spec/or :event :reframe/sub-or-event
                                 :fn fn?))

(spec/def ::validation-value-fn ifn?)
(spec/def ::control-props map?)
(spec/def ::form-field-props map?)

(spec/def ::subscription (spec/or :subscription :reframe/sub-or-event
                                  :atom rfi/ratom?))

(spec/def ::on-save ::fn-or-event)
(spec/def ::on-change ::fn-or-event)

(spec/def ::props
  (spec/keys :opt
             [:entity/ref :validation/schema :validation/stages
              ::validation-value-fn ::control-props ::form-field-props
              ::on-change ::on-save ::subscription]))

(defn- add-subscription-change
  [sub on-change]
  (add-watch sub :subscription-change
             (fn [_id _atom old new]
               (when (not= old new) (on-change new)))))

(defn- remove-subscription-change
  [sub]
  (remove-watch sub :subscription-change))

(defn- dispatch-or-call
  [fn-or-event & args]
  (case (first (spec/conform ::fn-or-event fn-or-event))
    :fn (apply fn-or-event args)
    :event (rf/dispatch (into fn-or-event args))))

(defn- with-initial-value-props
  "Adds value-props keys to props"
  [view-fn]
  (fn [props & args]
    {:pre [(spec/assert ::props props)]}
    (let [value (r/atom "")
          value-props #:value-props
                      {:value value
                       :save-value! identity
                       :reset-value identity
                       :update-value
                       (fn [v]
                         (reset! value v)
                         (when-let [on-change (:on-change props)]
                           (dispatch-or-call on-change v)))}]
      (into [view-fn]
            (conj args (merge props value-props))))))

(defn- with-subscription
  "Update value when subscription value changes"
  [view-fn]
  (fn [{:keys [subscription]
        :entity/keys [ref]
        :value-props/keys [update-value]
        :as props} & args]
    (r/with-let [subscription (when subscription
                                (case (first (spec/conform ::subscription subscription))
                                  :atom subscription
                                  :subscription (rf/subscribe (conj (vec subscription) ref))))
                 props (cond-> props
                         (some? subscription)
                         (assoc :value-props/reset-value #(update-value @subscription)))
                 _ (when subscription
                     (update-value @subscription)
                     (add-subscription-change subscription (partial update-value)))]
      (into [view-fn] (conj args props))
      (finally
        (when subscription
          (remove-subscription-change subscription))))))

(defn- with-save-when-valid
  "Adds check and save"
  [view-fn]
  (fn [{:entity/keys [ref parent]
        validation-schema :validation/schema
        :value-props/keys [reset-value]
        :keys [on-save validation-value-fn]
        :as props} & args]
    (let [props (assoc props
                       :value-props/save-value!
                       (fn [new-value]
                         (let [value ((or validation-value-fn identity) new-value)]
                           ;; note: this is just a snapshot, not reactive
                           (if @(rf/subscribe [:validation/results-filtered-by-stage
                                               (:eid parent) (:eid ref) validation-schema value #{:save-blocking}])
                             (reset-value)
                             (when on-save (dispatch-or-call on-save new-value))))))]
      (into [view-fn] (conj args props)))))

(defn with-validation-result
  "Adds validation results to props"
  [view-fn]
  (fn [{:entity/keys [ref parent]
        :value-props/keys [value]
        :keys [validation-value-fn]
        validation-schema :validation/schema
        validation-stages :validation/stages
        validation-disabled? :validation/disabled?
        :or {validation-stages #{:save-blocking}
             validation-disabled? false}
        :as props} & args]
    (let [validation-value ((or validation-value-fn identity) @value)
          props (cond-> props
                  (not validation-disabled?) (assoc :validation-result
                                                    @(rf/subscribe [:validation/results-filtered-by-stage
                                                                    (:eid parent) (:eid ref) validation-schema validation-value validation-stages])))]
      (into [view-fn] (conj args props)))))

(defn- with-validation-feedback
  "Adds validation with form-field"
  [view-fn]
  (fn [{:keys [validation-result form-field-props]
        :as props} & args]
    ($ form-field
       (merge form-field-props
              (util/map-vals (partial map :message) validation-result))
       (r/as-element
        (into [view-fn] (conj args props))))))

(def with-value-props
  "Higher order component.
   Assoc `:value-props` into props (first arity)
   to control the input value."
  (comp with-initial-value-props
        with-subscription
        with-save-when-valid
        with-validation-result
        with-validation-feedback))

;; ----- Props ------- ;;

(defn props-entity
  "Default input props for an ds entity."
  ([ref] (props-entity ref nil))
  ([ref attr]
   (cond-> {:entity/ref ref :key (pr-str [ref attr])}
     (some? attr) (assoc :subscription (store/get-value ref attr)
                         :validation/schema attr
                         :on-save (partial store/set-value! ref attr)))))

(defn props-input-integer
  "Default input props for an ds entity where
   its attribute value is a number."
  ([ref] (props-input-integer ref nil))
  ([ref attr]
   (assoc (props-entity ref attr)
          :control-props {:type :number}
          :validation/schema
          [:int {:error/message "Should be an integer"}]
          :validation-value-fn util/str->int
          :on-save #(store/set-value! ref attr (util/str->int %)))))

(rf/reg-sub ::get-in-path
  (fn [[_ pr attr _path]]
    (store/get-value pr attr))
  (fn [value [_ _pr _attr path]]
    (get-in value path)))

(defn props-path
  ([ref] (props-path ref nil))
  ([ref attr] (props-path ref attr []))
  ([ref attr path] (props-path ref attr path identity))
  ([ref attr path cast]
   {:subscription [::get-in-path ref attr path]
    :on-save #(let [ds-value @(store/get-value ref attr)]
                (store/set-value! ref attr
                                  (if (map? ds-value)
                                    (assoc-in ds-value path (cast %))
                                    (cast %))))}))

(defn props-input-timestamp
  "Default input props for an ds entity where
   its attribute value is a timestamp."
  ([ref] (props-input-timestamp ref nil))
  ([ref attr]
   (assoc (props-entity ref attr)
          :control-props {:type :datetime}
          :validation/schema
          [:string {:min 1 :error/message "Should be a valid date"}])))

(defn props-input-time
  "Default input props for an ds entity where
   its attribute value is a time."
  ([ref] (props-input-time ref nil))
  ([ref attr]
   (assoc (props-entity ref attr)
          :control-props {:type :time}
          :validation/schema
          [:map {:error/message "Should be a valid time"}
           [:minute [:int {:min 0 :max 59}]
            :hour [:int {:min 0 :max 23}]]])))

(defn props-tag-input
  "Default input props for an ds entity where
   its attribute value is a collection of tags."
  ([ref] (props-tag-input ref nil))
  ([ref attr]
   (assoc (props-entity ref attr)
          :control-props {:multi true :clearable true})))

;; ----- input elements ----- ;;

(def input-save-on-enter
  "Renders an instance of HTMLInputElement.
   Tries to save current value on blur input."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ input
         (merge control-props
                {:value @value
                 :onChange #(update-value (.. % -target -value))
                 :onKeyPress #(when (= (.-key %) "Enter")
                                (.preventDefault %)
                                (save-value! (.. % -target -value)))})))))

(def input-save-on-change
  "Renders an instance of HTMLInputElement.
   Tries to update- and save current value on changes."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ input
         (merge (cond-> control-props
                  (:disabled control-props)
                  (assoc :sx #js {".MuiInputBase-input" #js {:cursor "not-allowed"}}))
                {:value @value
                 :onChange #(let [v (.. % -target -value)]
                              (update-value v)
                              (save-value! v))})))))

(def input-save-on-blur
  "Renders an instance of HTMLInputElement.
   Tries to save current value on keypress ENTER."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ input
         (merge (cond-> control-props
                  (:disabled control-props)
                  (assoc :sx #js {".MuiInputBase-input" #js {:cursor "not-allowed"}}))
                {:value @value
                 :onChange #(update-value (.. % -target -value))
                 :onBlur #(save-value! (.. % -target -value))})))))

(def dropdown-save-on-change
  "Renders a dropdown.
   Tries to save current value when value has changed."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :dropdown/keys [cast-value-fn options-sub]
          :keys [control-props]}]
      [dropdown/dropdown
       (cond->
         (merge (rename-keys control-props {:multi :multi? :clearable :clearable?})
                {:value ((or cast-value-fn identity) @value)
                 :on-change (fn [v]
                              (update-value v)
                              (save-value! v))})
         (some? options-sub)
         (assoc :options @(rf/subscribe options-sub)))])))

(def datetime-picker-save-on-change
  "Renders a datepicker.
   Tries to save selected value when calendar is closed."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ datetime-picker
         (-> control-props
             (assoc :value @value
                    :onChange #(update-value %)
                    :onClose #(save-value! @value)
                    :onBlur #(save-value! @value)
                    :sx #js {:width "100%"}))))))

(def time-picker-save-on-change
  "Renders a time picker.
   Tries to save selected value when calendar is closed."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ time-picker
         (merge (rename-keys control-props {:disabled? :disabled})
                {:format "HH:mm"
                 :value (util/timemap->moment @value)
                 :onChange #(update-value (util/moment->timemap %))
                 :onClose #(save-value! @value)
                 :onBlur #(save-value! @value)
                 :sx #js {:width "100%"}})))))

(def tags-input-save-on-change
  "Renders a tags input.
   Tries to save selected value when value is created or deleted."
  (with-value-props
    (fn [{:value-props/keys [update-value value save-value!]
          :keys [control-props]}]
      ($ tag-input
         (-> control-props
             (dissoc :multi :clearable)
             (assoc :value @value
                    :multi? (:multi control-props)
                    :clearable? (:clearable control-props)
                    :on-change (fn [v]
                                 (update-value v)
                                 (save-value! v))))))))
